import React, { useEffect, useRef, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import HTMLFlipBook from 'react-pageflip';
import '../styles/preview.css';


interface PreviewImageModalProps {
  isOpen: boolean;
  onClose: () => void;
  imageUrl: string[];
}

type Slide = { type: 'image'; url: string } | { type: 'blank' };

// react-pageflip needs each page to forward its ref to a DOM node
const BookPage = React.forwardRef<HTMLDivElement, { slide: Slide; index: number }>(({ slide, index }, ref) => (
  <div ref={ref} className="page">
    <div className="page-content">
      <div
        className="page-image"
        role={slide.type === 'image' ? 'img' : undefined}
        aria-label={slide.type === 'image' ? `Page ${index + 1}` : undefined}
        style={slide.type === 'image' ? { backgroundImage: `url("${slide.url}")` } : undefined}
      />
      <div className="page-footer">{slide.type === 'image' ? index + 1 : ''}</div>
    </div>
  </div>
));

// Fixed page size (px). Only scaled down, keeping this shape, when the screen is too small for it.
const PAGE_WIDTH = 700;
const PAGE_HEIGHT = 954;
// Space kept clear around the book inside the viewport, and taken by the two arrow buttons
const SCREEN_MARGIN = 32;
const ARROWS_WIDTH = 2 * 44 + 2 * 8;

const ArrowButton: React.FC<{ onClick: () => void; disabled: boolean; label: string; children: React.ReactNode }> = ({ onClick, disabled, label, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    className="shrink-0 p-2 rounded-full text-white bg-black/30 hover:bg-black/50 disabled:opacity-30 disabled:cursor-default"
  >
    {children}
  </button>
);

const PreviewImageModal: React.FC<PreviewImageModalProps> = ({ isOpen, onClose, imageUrl }) => {
  const bookRef = useRef<any>(null);
  const [page, setPage] = useState(0);
  const [viewport, setViewport] = useState({ w: window.innerWidth, h: window.innerHeight });

  useEffect(() => {
    const onResize = () => setViewport({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const slides: Slide[] = imageUrl.map<Slide>((url) => ({ type: 'image', url }));
  // Pad odd page counts so the last two-page spread isn't half empty
  if (slides.length % 2 === 1) slides.push({ type: 'blank' });

  const pageRatio = PAGE_WIDTH / PAGE_HEIGHT;

  // Fixed page size, shrunk only if the screen can't fit it. The container width is set explicitly
  // because in "stretch" mode the library sizes pages from it and drops to one page when it's too narrow.
  const singlePage = viewport.w < 768;
  const pagesShown = singlePage ? 1 : 2;
  const maxBookW = viewport.w - SCREEN_MARGIN * 2 - ARROWS_WIDTH;
  const maxPageH = viewport.h - SCREEN_MARGIN * 2;
  const pageW = Math.max(Math.min(PAGE_WIDTH, maxBookW / pagesShown, maxPageH * pageRatio), 150);
  const bookW = Math.round(pageW * pagesShown);

  // Spread shows pages `page` and `page + 1`
  const isFirst = page <= 0;
  const isLast  = page + pagesShown >= slides.length;

  return (
    <Transition appear show={isOpen} as={React.Fragment} afterLeave={() => setPage(0)}>
      {/* Above the app Header (z-index 50) and its dropdown (100), which otherwise cover the top of the book */}
      <Dialog as="div" className="relative z-[200]" onClose={onClose}>
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-60 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center" style={{ padding: SCREEN_MARGIN }}>
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel aria-label="Album preview" className="relative flex items-center gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Close preview"
                  className="fixed top-3 right-3 z-20 p-1.5 rounded-full text-white bg-black/30 hover:bg-black/50 focus:outline-none"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                </button>

                <ArrowButton onClick={() => bookRef.current?.pageFlip()?.flipPrev()} disabled={isFirst} label="Previous page">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
                </ArrowButton>

                <div className="shrink-0" style={{ width: bookW }}>
                  {slides.length > 0 && (
                    <HTMLFlipBook
                      // Remount on resize: the library only reads its settings once
                      key={bookW}
                      ref={bookRef}
                      className="flip-book"
                      style={{}}
                      startPage={page}
                      size="stretch"
                      width={PAGE_WIDTH}
                      height={PAGE_HEIGHT}
                      // The library goes single-page when the container is under 2 × minWidth
                      minWidth={singlePage ? Math.ceil(bookW / 2) + 1 : 150}
                      maxWidth={1000}
                      minHeight={Math.round(150 / pageRatio)}
                      maxHeight={Math.round(1000 / pageRatio)}
                      drawShadow={true}
                      flippingTime={1000}
                      usePortrait={true}
                      startZIndex={0}
                      autoSize={true}
                      maxShadowOpacity={0.5}
                      showCover={false}
                      mobileScrollSupport={true}
                      clickEventForward={false}
                      useMouseEvents={true}
                      swipeDistance={30}
                      showPageCorners={true}
                      disableFlipByClick={false}
                      onFlip={(e: any) => setPage(e.data)}
                    >
                      {slides.map((slide, index) => (
                        <BookPage key={index} slide={slide} index={index} />
                      ))}
                    </HTMLFlipBook>
                  )}
                </div>

                <ArrowButton onClick={() => bookRef.current?.pageFlip()?.flipNext()} disabled={isLast} label="Next page">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
                </ArrowButton>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default PreviewImageModal;
